import React, { useEffect, useMemo, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import LogoMain from '../LogoMain.png';
import { authService } from '../services/authService';
import { getApiBase } from '../config/apiBase';

// "Request access to Lyric" screen. Reached from the login page after the user
// authenticates via Okta (the site is public-facing, so we require Okta before
// letting anyone email a request). Submitting POSTs to /api/access-request,
// which emails the IT/admin inbox through Lyric's SMTP relay.
const RequestAccess = () => {
  const { oktaAuth, authState } = useOktaAuth();

  const claims = useMemo(
    () => (authState && authState.idToken && authState.idToken.claims) || {},
    [authState],
  );

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [team, setTeam] = useState('');
  const [role, setRole] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sentTo, setSentTo] = useState(null);

  // Prefill identity from the Okta ID token once it resolves.
  useEffect(() => {
    if (claims.name) setName((prev) => prev || claims.name);
    if (claims.email) setEmail((prev) => prev || claims.email);
  }, [claims]);

  const apiBase = getApiBase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please tell us why you need access.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = await authService.getAccessToken();
      const res = await fetch(`${apiBase}/api/access-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, team, role, reason }),
      });
      const raw = await res.text();
      let data;
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error(`Server returned a non-JSON response (HTTP ${res.status}). If Lyric was just updated, try again shortly.`);
      }
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || `Request failed (HTTP ${res.status})`);
      }
      setSentTo(data.sentTo || 'the Lyric team');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    try { sessionStorage.setItem('lyric.justLoggedOut', '1'); } catch { /* ignore */ }
    // Match LandingPage/UserProfile: destroy the Lyric server session cookie
    // and clear the Okta token cache before the signOut redirect, so no tokens
    // linger across the bounce-back.
    await authService.destroySession();
    try { await oktaAuth.tokenManager.clear(); } catch { /* ignore */ }
    oktaAuth.signOut();
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <img src={LogoMain} alt="RapidAI Logo" style={styles.logo} />
          <h1 style={styles.title}>Lyric</h1>
        </div>

        {sentTo ? (
          <div style={styles.successBox}>
            <div style={styles.successIcon}>✅</div>
            <h2 style={styles.successTitle}>Request sent</h2>
            <p style={styles.successText}>
              Thanks{name ? `, ${name.split(' ')[0]}` : ''}! Your access request has
              been sent to {sentTo}. Someone will follow up by email.
            </p>
            <button onClick={handleSignOut} style={styles.secondaryButton}>Sign out</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <h2 style={styles.formTitle}>Request access to Lyric</h2>
            <p style={styles.formSubtitle}>
              You're signed in with Okta. Tell us a little about your request and
              we'll email the Lyric team.
            </p>

            <label style={styles.label}>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              style={styles.input}
            />

            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@ischemaview.com"
              style={styles.input}
              readOnly={!!claims.email}
            />

            <div style={styles.row}>
              <div style={styles.rowCol}>
                <label style={styles.label}>Team / Department <span style={styles.optional}>(optional)</span></label>
                <input
                  type="text"
                  value={team}
                  onChange={(e) => setTeam(e.target.value)}
                  placeholder="e.g. Clinical Engineering"
                  style={styles.input}
                />
              </div>
              <div style={styles.rowCol}>
                <label style={styles.label}>Role / access needed <span style={styles.optional}>(optional)</span></label>
                <input
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="e.g. Reviewer, Admin"
                  style={styles.input}
                />
              </div>
            </div>

            <label style={styles.label}>Reason for access <span style={styles.required}>*</span></label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What do you plan to use Lyric for?"
              rows={4}
              style={styles.textarea}
            />

            {error && <p style={styles.error}>⚠️ {error}</p>}

            <button type="submit" disabled={submitting} style={{ ...styles.primaryButton, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}>
              {submitting ? 'Sending…' : 'Send access request'}
            </button>
            <button type="button" onClick={handleSignOut} style={styles.linkButton}>
              Cancel and sign out
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '2rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    maxWidth: '560px',
    width: '100%',
    padding: '2.5rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  logo: { width: '44px', height: 'auto' },
  title: { fontSize: '2rem', margin: 0, fontWeight: 'bold', color: '#333' },
  form: { display: 'flex', flexDirection: 'column' },
  formTitle: { fontSize: '1.5rem', color: '#333', margin: '0 0 0.5rem', fontWeight: 'bold' },
  formSubtitle: { color: '#666', margin: '0 0 1.5rem', lineHeight: 1.5 },
  label: { fontSize: '0.9rem', fontWeight: 600, color: '#333', marginBottom: '0.35rem' },
  optional: { fontWeight: 400, color: '#999' },
  required: { color: '#dc3545' },
  input: {
    padding: '0.7rem 0.8rem',
    border: '1px solid #ced4da',
    borderRadius: '8px',
    fontSize: '1rem',
    marginBottom: '1rem',
  },
  textarea: {
    padding: '0.7rem 0.8rem',
    border: '1px solid #ced4da',
    borderRadius: '8px',
    fontSize: '1rem',
    marginBottom: '1rem',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  row: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  rowCol: { flex: '1 1 200px', display: 'flex', flexDirection: 'column' },
  error: { color: '#dc3545', fontSize: '0.9rem', margin: '0 0 1rem' },
  primaryButton: {
    backgroundColor: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.85rem 1.5rem',
    fontSize: '1.05rem',
    fontWeight: 'bold',
    marginBottom: '0.75rem',
  },
  secondaryButton: {
    backgroundColor: '#6c757d',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '1rem',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#667eea',
    fontSize: '0.95rem',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: '0.25rem',
  },
  successBox: { textAlign: 'center', padding: '1rem 0' },
  successIcon: { fontSize: '3rem', marginBottom: '0.5rem' },
  successTitle: { fontSize: '1.6rem', color: '#28a745', margin: '0 0 0.75rem', fontWeight: 'bold' },
  successText: { color: '#555', lineHeight: 1.6, margin: '0 auto', maxWidth: '400px' },
};

export default RequestAccess;
